/** @format */

import { useState, useCallback } from "react";
import type { Brand, QuestionStatus } from "@question-bank/shared";
import { updateQuestion } from "../api/questions";

interface UseUpdateQuestionStatusReturn {
	isUpdating: boolean;
	error: string | null;
	updateQuestionStatus: (questionId: string, status: QuestionStatus, rejectionReason?: string) => Promise<boolean>;
	reset: () => void;
}

export const useUpdateQuestionStatus = (brand: Brand): UseUpdateQuestionStatusReturn => {
	const [isUpdating, setIsUpdating] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const updateQuestionStatus = useCallback(
		async (questionId: string, status: QuestionStatus, rejectionReason?: string): Promise<boolean> => {
			setIsUpdating(true);
			setError(null);
			try {
				await updateQuestion(questionId, brand, {
					status,
					...(rejectionReason !== undefined ? { rejectionReason } : {}),
				});
				return true;
			} catch (err) {
				setError(err instanceof Error ? err.message : "Status update failed");
				return false;
			} finally {
				setIsUpdating(false);
			}
		},
		[brand],
	);

	const reset = useCallback(() => setError(null), []);

	return { isUpdating, error, updateQuestionStatus, reset };
};
